import { useState } from "react";
import { ethers } from "ethers";
import {
  evmGetAddress,
  KeyfobSigner,
  setUserConfirmationCallbacks,
} from "@gardio/device";
import {
  defaultTokenAddress,
  ERC20_ABI,
  tokenRecordFor,
  getTokenBalanceOf,
  getRpcUrl,
} from "@gardio/contracts/polygon";
import type { Network } from "@gardio/contracts/polygon";
import type { AccountWithBalance } from "./useAccountFetcher";
import { TopUpStep } from "./useTopUp";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "utils/errors/parseApiError";

export type SigningPhase =
  | "idle"
  | "checking"
  | "waiting_confirmation"
  | "signing"
  | "confirm_ready"
  | "broadcasting";

export const SIGNING_PHASE_LABEL: Record<SigningPhase, string> = {
  idle: "",
  checking: "Checking…",
  waiting_confirmation: "Waiting user confirmation…",
  signing: "Signing transaction…",
  confirm_ready: "Transaction signed",
  broadcasting: "Broadcasting transaction…",
};

export interface UseTopUpSigningReturn {
  signingPhase: SigningPhase;
  txHash: string;
  showError: boolean;
  errorMsg: string;
  showSuccess: boolean;
  isBusy: boolean;
  /**
   * True when this transfer is being signed with NO device record, so the Keyfob displays nothing
   * about it. Testnet only. The UI must say so rather than let it pass silently — the whole point
   * of the device is that it, not the host screen, shows you what you are approving.
   */
  blindSigning: boolean;
  handleSign: () => Promise<void>;
  handleBroadcast: (pendingSignedTx?: string) => Promise<void>;
  handleCloseError: () => void;
  handleSuccessClose: () => void;
  resetSigning: () => void;
}

export function useTopUpSigning(
  network: Network,
  amount: string,
  cardAliasAddress: string,
  setPhase: (ph: TopUpStep) => void,
  account?: AccountWithBalance,
): UseTopUpSigningReturn {
  const {
    param: selectedTokenAddress,
    label: selectedTokenLabel,
  } = useSettlementToken();
  const queryClient = useQueryClient();
  const [signingPhase, setSigningPhase] = useState<SigningPhase>("idle");
  const [pendingSignedTx, setPendingSignedTx] = useState<string | null>(null);
  const [txHash, setTxHash] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [blindSigning, setBlindSigning] = useState(false);

  const clearCallbacks = () =>
    setUserConfirmationCallbacks(
      () => {},
      () => {},
    );

  const openError = (msg: string) => {
    clearCallbacks();
    setSigningPhase("idle");
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleSign = async () => {
    try {
      setShowError(false);
      setPhase(2);
      setSigningPhase("checking");
      // The token being topped up is the SELECTED one, not the network default. Everything below
      // — the record, the balance check, the decimals and the transfer target — keys off this.
      const tokenAddress =
        selectedTokenAddress ?? defaultTokenAddress(network);

      // Without a signed record the device signs BLIND: evm/signing.ts sets
      // `isToken = tokenBinary ? 1 : 0`, so a missing blob makes the Keyfob sign the transfer as
      // a plain transaction and display nothing about the token while the holder approves it.
      //
      // TESTNET ONLY, and deliberately NOT behind an env flag. `network` is a BUILD constant —
      // utils/network.ts throws at build time unless REACT_APP_POLYGON_NETWORK is testnet or
      // mainnet, and the backend independently rejects the other network's traffic via
      // DEPLOY_NETWORK_ENFORCE. So this branch is structurally unreachable in a mainnet build,
      // which is a harder fence than a flag someone can set by mistake; a flag would add no
      // mainnet safety, only a second thing to get wrong.
      //
      // This exists because the vendor has not issued an Amoy USDC record. Paste that blob into
      // TOKEN_RECORDS.testnet and this branch stops being taken on its own — nothing else needs
      // removing.
      const tokenRecord = tokenRecordFor(network, tokenAddress);
      const signingBlind = !tokenRecord && network === "testnet";
      if (!tokenRecord && !signingBlind) {
        openError(
          `${selectedTokenLabel} has no signed device record, so the Keyfob cannot show what ` +
            `it is signing. Refusing to sign. Top up in a token that has one.`,
        );
        return;
      }
      setBlindSigning(signingBlind);
      if (signingBlind) {
        // Also logged, so it is visible in a bug report or session recording where the on-screen
        // warning was dismissed.
        console.warn(
          `[top-up] No signed device record for ${selectedTokenLabel} on ${network}. Signing as ` +
            `a PLAIN transaction — the Keyfob will not display the token, amount or recipient.`,
        );
      }

      const derived = await evmGetAddress(0, 1);
      const balance = await getTokenBalanceOf(
        derived[0].address,
        network,
        tokenAddress,
      );
      const withBalance: AccountWithBalance = { ...derived[0], balance };
      const account = withBalance;
      const provider = new ethers.JsonRpcProvider(getRpcUrl(network));
      const signer = new KeyfobSigner({ ...account }, provider, tokenRecord);

      // Wire device confirmation callbacks to phase transitions
      setUserConfirmationCallbacks(
        () => setSigningPhase("waiting_confirmation"),
        () => setSigningPhase("signing"),
      );

      const token = new ethers.Contract(tokenAddress, ERC20_ABI, provider);

      // No 18-decimal fallback: a 6-decimal token scaled as 18 would send 10^12 times the
      // intended amount. If decimals cannot be read, stop.
      let decimals: number;
      try {
        decimals = Number(await token.decimals());
      } catch (_) {
        openError(
          `Could not read decimals for ${selectedTokenLabel}. Refusing to sign rather than ` +
            `guess the amount.`,
        );
        return;
      }

      const amountBN = ethers.parseUnits(amount, decimals);

      const [maticBalance, tokenBalance] = await Promise.all([
        provider.getBalance(account.address),
        token.balanceOf(account.address),
      ]);

      if (maticBalance === BigInt(0)) {
        throw new Error(
          "Insufficient MATIC for gas fees. Fund this address with MATIC before sending.",
        );
      }
      if (tokenBalance < amountBN) {
        const human = parseFloat(ethers.formatUnits(tokenBalance, decimals));
        throw new Error(
          `Insufficient ${selectedTokenLabel} balance. Available: ` +
            `${human.toFixed(4)} ${selectedTokenLabel}`,
        );
      }

      // Build token transfer calldata
      const iface = new ethers.Interface(ERC20_ABI);
      const data = iface.encodeFunctionData("transfer", [
        cardAliasAddress,
        amountBN,
      ]);

      // Populate tx fields (nonce, gas, chainId…) then sign on device.
      // populateTransaction adds `from`, but ethers v6 signTransaction rejects
      // it on unsigned transactions — strip it before handing off to the signer.
      setSigningPhase("waiting_confirmation");
      const populated = await signer.populateTransaction({
        to: tokenAddress,
        data,
      });
      const { from: _from, ...txToSign } = populated as any;
      const serialized = await signer.signTransaction(
        txToSign as ethers.TransactionRequest,
      );

      clearCallbacks();
      setPendingSignedTx(serialized);
      setPhase(3);
      handleBroadcast(serialized);
      // setSigningPhase("confirm_ready");
    } catch (err: any) {
      openError(apiErrorMessage(err, "Signing failed. Please try again."));
    }
  };

  const handleBroadcast = async (pendingSignedTx?: string) => {
    if (!pendingSignedTx) return;
    setPhase(3);
    setSigningPhase("broadcasting");

    try {
      const provider = new ethers.JsonRpcProvider(getRpcUrl(network));
      const txResponse = await provider.broadcastTransaction(pendingSignedTx);
      const receipt = await txResponse.wait(1);
      const hash = receipt?.hash ?? txResponse.hash;
      queryClient.invalidateQueries({
        queryKey: ["balances"],
      });
      queryClient.invalidateQueries({
        queryKey: ["deployedBalance"],
      });
      // A card reached by SEARCH is not in ["balances"] — /lookupCard carries its balance inside
      // the card record, so without this the row the top-up was launched from is the one row that
      // keeps showing the old figure. invalidate refetches active queries regardless of staleTime.
      queryClient.invalidateQueries({
        queryKey: ["card-lookup"],
      });
      setTxHash(hash);
      setSigningPhase("idle");
      setPhase(4);
      setShowSuccess(true);
    } catch (err: any) {
      openError(
        apiErrorMessage(err, "Transaction broadcast failed. Please try again."),
      );
    }
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleSuccessClose = () => {
    resetSigning();
  };

  const resetSigning = () => {
    clearCallbacks();
    setSigningPhase("idle");
    setPendingSignedTx(null);
    setTxHash("");
    setShowError(false);
    setErrorMsg("");
    setShowSuccess(false);
    // Cleared with the rest of the run, so a later top-up in a token that HAS a record cannot
    // inherit the warning from this one.
    setBlindSigning(false);
  };

  const isBusy = signingPhase !== "idle" && signingPhase !== "confirm_ready";

  return {
    signingPhase,
    txHash,
    showError,
    errorMsg,
    showSuccess,
    isBusy,
    blindSigning,
    handleSign,
    handleBroadcast,
    handleCloseError,
    handleSuccessClose,
    resetSigning,
  };
}
